/**
 * CitationChip — clickable [N] chip + inline metadata card.
 *
 * Sprint AI-MODULE v2.1 AI-41 / AI-45. Default state is the same compact
 * chip we shipped in v2. For ``kind === 'cw_file'`` clicking the chevron
 * expands an inline card with mime icon, owner, modified, size, parent
 * folder, and (where the mime type supports it) a Google Drive / M365
 * iframe preview. Non-file citations keep the v2 chip behavior.
 */
import { useState, type FC } from 'react';
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Eye,
  ExternalLink,
  FileSpreadsheet,
  FileText,
  Presentation,
  Image as ImageIcon,
  File as FileIcon,
  Lock,
} from 'lucide-react';
import type { CitationRef } from '../api/aiApi';

interface Props {
  citation: CitationRef;
}

// Kinds that render as a rich card (with the chip + expander) rather
// than just a thin chip. Each card variant decides its own metadata
// + action button.
const CARD_KINDS = new Set([
  'cw_file', 'dm_document',
  // v2.3 (AI-61): VM citations also expand into rich cards. The chip
  // shows the verbose state; AssistantText is what emits the
  // VmInlineCard with action CTAs.
  'vm_vendor', 'vm_pr', 'vm_contract', 'vm_po',
]);

const KIND_LABEL: Record<string, string> = {
  cw_file: 'File',
  cm_page: 'Page',
  vm_contract: 'Contract',
  vm_pr: 'PR',
  vm_po: 'PO',
  itsd_ticket: 'Ticket',
  dm_document: 'Doc',
  calendar_event: 'Event',
  attention_item: 'Attention',
  platform: 'Platform',
};

const CitationChip: FC<Props> = ({ citation }) => {
  // Inline cards in the message body are the primary presentation
  // for card-kind citations now (see AssistantText + InlineCitationCard).
  // The sources list at the bottom is the audit/full list — stays
  // collapsed by default.
  const [open, setOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const isCard = CARD_KINDS.has(citation.kind);
  const isFile = citation.kind === 'cw_file';
  const isDmDoc = citation.kind === 'dm_document';
  const isExternal = /^https?:\/\//i.test(citation.url);
  const kindLabel = KIND_LABEL[citation.kind] ?? citation.kind;
  const previewUrl = isFile ? buildPreviewUrl(citation) : null;

  const chipBody = (
    <>
      <span className="ai-citation__id">[{citation.id}]</span>
      {mimeIcon(citation.mime_type, 11)}
      <span className="ai-citation__label">{citation.label}</span>
      <span className="ai-citation__kind">{kindLabel}</span>
      {isExternal && <ExternalLink size={10} />}
    </>
  );
  const title = citation.snippet || `${kindLabel}: ${citation.label}`;

  // Non-card kinds keep the thin chip shape.
  if (!isCard) {
    return isExternal ? (
      <a className="ai-citation" href={citation.url} target="_blank" rel="noopener noreferrer" title={title}>
        {chipBody}
      </a>
    ) : (
      <a className="ai-citation" href={citation.url} title={title}>
        {chipBody}
      </a>
    );
  }

  // Card kinds: chip-with-expander.
  return (
    <span className={'ai-citation-wrap' + (open ? ' is-open' : '')}>
      <span className="ai-citation ai-citation--file" title={title}>
        <a
          className="ai-citation__link"
          href={citation.url}
          target={isExternal ? '_blank' : undefined}
          rel={isExternal ? 'noopener noreferrer' : undefined}
        >
          {chipBody}
        </a>
        <button
          type="button"
          className="ai-citation__toggle"
          aria-expanded={open}
          aria-label={open ? 'Hide details' : 'Show details'}
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
        </button>
      </span>
      {open && (
        <div className="ai-citation-card">
          <div className="ai-citation-card__head">
            {isDmDoc ? <Lock size={16} /> : mimeIcon(citation.mime_type, 16)}
            <div className="ai-citation-card__title">{citation.label}</div>
          </div>
          <dl className="ai-citation-card__meta">
            {/* cw_file metadata. */}
            {isFile && citation.parent_name && (
              <>
                <dt>Folder</dt>
                <dd>{citation.parent_name}</dd>
              </>
            )}
            {isFile && citation.owner_name && (
              <>
                <dt>Owner</dt>
                <dd>{citation.owner_name}</dd>
              </>
            )}
            {citation.last_modified && (
              <>
                <dt>{isDmDoc ? 'Published' : 'Modified'}</dt>
                <dd>{formatRelative(citation.last_modified)}</dd>
              </>
            )}
            {isFile && citation.file_size_bytes != null && (
              <>
                <dt>Size</dt>
                <dd>{formatBytes(citation.file_size_bytes)}</dd>
              </>
            )}
            {isFile && citation.mime_type && (
              <>
                <dt>Type</dt>
                <dd>{prettyMime(citation.mime_type)}</dd>
              </>
            )}
            {citation.provider && (
              <>
                <dt>Source</dt>
                <dd>{isDmDoc ? 'Document Management' : citation.provider}</dd>
              </>
            )}
          </dl>
          <div className="ai-citation-card__actions">
            {isDmDoc ? (
              <a
                className="btn btn--sm btn--primary"
                href={citation.url}
              >
                <CheckCircle2 size={12} /> Acknowledge now
              </a>
            ) : (
              <a
                className="btn btn--sm btn--secondary"
                href={citation.url}
                target={isExternal ? '_blank' : undefined}
                rel={isExternal ? 'noopener noreferrer' : undefined}
              >
                <ExternalLink size={12} /> Open in {citation.provider ?? 'source'}
              </a>
            )}
            {isFile && previewUrl ? (
              <button
                type="button"
                className="btn btn--sm btn--ghost"
                onClick={() => setPreviewOpen((v) => !v)}
              >
                <Eye size={12} /> {previewOpen ? 'Hide preview' : 'Preview'}
              </button>
            ) : isFile ? (
              <span className="ai-citation-card__no-preview">
                Inline preview not available for this file type
              </span>
            ) : null}
          </div>
          {previewOpen && previewUrl && (
            <iframe
              className="ai-citation-card__preview"
              src={previewUrl}
              title={`Preview of ${citation.label}`}
              sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
              loading="lazy"
            />
          )}
        </div>
      )}
    </span>
  );
};

export default CitationChip;


// ── helpers ──────────────────────────────────────────────────────────

function mimeIcon(mime: string | undefined, size: number) {
  if (!mime) return <FileText size={size} />;
  if (mime.includes('spreadsheet') || mime.includes('excel') || mime === 'text/csv') return <FileSpreadsheet size={size} />;
  if (mime.includes('presentation') || mime.includes('powerpoint')) return <Presentation size={size} />;
  if (mime.startsWith('image/')) return <ImageIcon size={size} />;
  if (mime.includes('document') || mime.includes('word') || mime === 'text/plain' || mime === 'application/pdf') return <FileText size={size} />;
  return <FileIcon size={size} />;
}

function prettyMime(mime: string): string {
  const map: Record<string, string> = {
    'application/vnd.google-apps.spreadsheet': 'Google Sheet',
    'application/vnd.google-apps.document': 'Google Doc',
    'application/vnd.google-apps.presentation': 'Google Slides',
    'application/vnd.google-apps.folder': 'Google Drive folder',
    'application/pdf': 'PDF',
    'text/plain': 'Text',
    'text/csv': 'CSV',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel workbook',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word document',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint',
  };
  return map[mime] ?? mime;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function formatRelative(isoTs: string): string {
  const t = new Date(isoTs);
  if (Number.isNaN(t.getTime())) return isoTs;
  const diffMs = Date.now() - t.getTime();
  const day = 24 * 60 * 60 * 1000;
  if (diffMs < day) return 'today';
  if (diffMs < 2 * day) return 'yesterday';
  if (diffMs < 7 * day) return `${Math.floor(diffMs / day)} days ago`;
  if (diffMs < 30 * day) return `${Math.floor(diffMs / (7 * day))} weeks ago`;
  return t.toISOString().slice(0, 10);
}

/**
 * Build an embeddable preview URL for supported provider+mime pairs.
 * Returns null when no clean inline preview is available — the card
 * falls back to "Open in {provider}".
 *
 * Google Drive: `/preview` works for Docs/Sheets/Slides; we transform
 * `https://docs.google.com/spreadsheets/d/{id}/edit?...` into
 * `https://docs.google.com/spreadsheets/d/{id}/preview`.
 *
 * M365 SharePoint: the cached `web_url` is usually a viewer link
 * already — `?action=embedview` or `&action=embedview` makes it
 * iframe-friendly. We keep a conservative whitelist (sharepoint.com /
 * onedrive.live.com hosts only).
 */
function buildPreviewUrl(c: CitationRef): string | null {
  if (!c.url) return null;
  const mime = c.mime_type ?? '';
  const provider = c.provider ?? '';

  if (provider === 'google_drive' && mime.startsWith('application/vnd.google-apps.')) {
    const driveMatch = c.url.match(/^(https:\/\/docs\.google\.com\/(?:spreadsheets|document|presentation)\/d\/[^/]+)/);
    if (driveMatch) return `${driveMatch[1]}/preview`;
    return null;
  }
  if (
    provider === 'microsoft365' &&
    (mime.includes('officedocument') || mime === 'application/pdf')
  ) {
    try {
      const u = new URL(c.url);
      if (u.hostname.endsWith('.sharepoint.com') || u.hostname === 'onedrive.live.com') {
        u.searchParams.set('action', 'embedview');
        return u.toString();
      }
    } catch {
      return null;
    }
  }
  return null;
}
